import {InputAdornment, TextField} from '@mui/material';
import {Control, FieldPath, FieldValues, RegisterOptions, useController} from 'react-hook-form';
import checkIcon from '../../../assets/demo-registration/check.svg';
import styles from '../DemoRegistration.module.scss';

interface Props<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  required?: boolean;
  placeholder?: string;
  rules?: Omit<RegisterOptions<TFieldValues, FieldPath<TFieldValues>>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
  startIcon?: string;
  transformValue?: (value: string) => string;
}

export const FormTextField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  required,
  placeholder,
  rules,
  startIcon,
  transformValue,
}: Props<TFieldValues>) => {
  const {
    field: {value, onChange, onBlur, ref},
    fieldState: {error, isTouched},
  } = useController({
    control,
    name,
    rules,
  });

  const hasValue = typeof value === 'string' && value.trim().length > 0;
  const showSuccess = hasValue && isTouched && !error;

  return (
    <div className={styles.fieldGroup}>
      <label htmlFor={String(name)} className={styles.fieldLabel}>
        {label}
        {required ? <span className={styles.requiredMark}>*</span> : null}
      </label>
      <TextField
        id={String(name)}
        inputRef={ref}
        className={styles.textField}
        size="small"
        fullWidth
        value={value}
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={(event) => onChange(transformValue ? transformValue(event.target.value) : event.target.value)}
        error={!!error}
        helperText={error?.message || ' '}
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment position="start">
              <span className={styles.leadingIconWrap}>
                <img src={startIcon} alt="" className={styles.leadingIcon} />
              </span>
            </InputAdornment>
          ) : undefined,
          endAdornment: showSuccess ? (
            <InputAdornment position="end">
              <img src={checkIcon} alt="" className={styles.validationIcon} />
            </InputAdornment>
          ) : undefined,
        }}
      />
    </div>
  );
};
